import { Trans } from 'react-i18next';

import {
   MedicalDocumentationGenerateCommand,
   MedicalDocumentationGenerationResult,
   RequiredIdentifierMissingFault,
} from '@dreryk/egabinet-visit-frontend-api';
import { MedicalDocumentSimpleQuery } from '@dreryk/egabinet-document-repository-frontend-api';

import { Range } from '@dreryk/watermelon';
import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { downloadDocumentAsPdf } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import { MedicalDocumentationCreateFormBean } from '../MedicalDocumentationCreateForm.types';
import { medicalDocumentationCreateTranslationKeys } from '../../../translations';

import { MyAccountLink } from './MyAccountLink';

type UseGenerateDocumentationFault = CertificateFault | RequiredIdentifierMissingFault;

type UseGenerateDocumentationCommandOptions = {
   patientId: number;
   encrypted: boolean;
   onAuthenticationRequired: VoidFunction;
};

export const useGenerateDocumentationCommand = (
   options: UseGenerateDocumentationCommandOptions
): UseCqrsCommandResult<
   MedicalDocumentationGenerationResult,
   MedicalDocumentationCreateFormBean,
   UseGenerateDocumentationFault
> => {
   return useCqrsCommand<
      MedicalDocumentationGenerationResult,
      MedicalDocumentationCreateFormBean,
      UseGenerateDocumentationFault
   >(
      (parameters) => {
         const command = new MedicalDocumentationGenerateCommand();
         command.patientId = options.patientId;
         command.createEncryptedCopy = options.encrypted;
         if (parameters.periodRange === 'SELECTED_RANGE') {
            command.dateRange = Range.closed(parameters.dateFrom!, parameters.dateTo!);
         }
         command.includeConfidentialDataAnnotation = parameters.includeConfidentialAnnotation;
         return command;
      },
      {
         onSuccess: (result) => {
            refetchCqrsQuery(MedicalDocumentSimpleQuery);
            downloadDocumentAsPdf({ base64: result.medicalDocumentationContent, fileName: result.fileName });
         },
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: options.onAuthenticationRequired,
            }),
            'egabinet-visit-frontend-api:RequiredIdentifierMissingFault': () => {
               dispatchSnackbarNegativeEvent(
                  <Trans
                     i18nKey={medicalDocumentationCreateTranslationKeys.unableToCreateDocumentationNoUserIdentifier}
                     components={{
                        link: <MyAccountLink />,
                     }}
                  />,
                  {
                     autoHideDelayInMs: false,
                  }
               );
            },
         },
      }
   );
};
